import CustomDialog from 'common/components/CustomDialog'
import useAnalytics, { AnalyticsEvents } from 'common/hooks/useAnalytics'
import useYupValidationResolver from 'common/hooks/useYupValidationResolver'
import { getRole } from 'modules/member/api'
import { useMutateCreateMembers, useMutateUpdateMembers } from 'modules/member/api/mutation'
import { useFarmGroup, useRoles } from 'modules/member/api/selector'
import { MemberScopeFarm, MemberScopePond, UpdateMembersBody } from 'modules/member/api/types'
import { CreateMemberDialogProps, FormDataType } from 'modules/member/api/types'
import { RoleName } from 'modules/member/constants'
import { transformFarmsData } from 'modules/member/utils'
import { useUserInfo } from 'modules/user/api/queries'
import { Permission } from 'modules/user/api/types'
import { useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { normalizeRoleForAnalytics } from 'utils/analytics'
import * as Yup from 'yup'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  ListItemText,
  InputAdornment,
  Stack,
  Grid,
  Divider,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'

import searchIcon from '@iconify/icons-ant-design/search'
import { Icon } from '@iconify/react'

const CreateMemberDialog = ({
  open,
  onClose,
  isEdit = false,
  initialMember,
  onSuccess,
}: CreateMemberDialogProps) => {
  const { t } = useTranslation(['common', 'member'])

  const title = isEdit ? t('member:edit.dialog.title') : t('member:add.dialog.title')
  const [permissionRole, setPermissionRole] = useState<Permission[]>([])
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState(search)
  const [farms, setFarms] = useState<MemberScopeFarm[]>([])
  const [selectedFarms, setSelectedFarms] = useState<MemberScopeFarm[]>([])
  const [isFarmsDirty, setIsFarmsDirty] = useState(false)

  const { data: roles } = useRoles()
  const secondaryOwnerRole = roles?.find((r) => r.name === RoleName.SecondaryOwner)
  const isOwnerMember =
    isEdit && !!initialMember?.is_owner && initialMember?.user_role_id !== secondaryOwnerRole?.id
  const { data: userInfo } = useUserInfo()
  const createMember = useMutateCreateMembers()
  const updateMember = useMutateUpdateMembers()
  const { trackEvent } = useAnalytics()

  const schema = Yup.object().shape({
    name: Yup.string().required(t('member:name.require.input')),
    lastname: Yup.string().required(t('member:lastname.require.input')),
    phone: Yup.string()
      .required(t('member:phone.require.input'))
      .matches(/^0[0-9]{9}$/, t('member:phone.invalid')),
    role: Yup.string().required(t('member:role.require.input')),
  })

  const resolver = useYupValidationResolver(schema)

  const {
    control,
    watch,
    setError,
    reset,
    formState: { errors, dirtyFields },
    trigger,
  } = useForm<FormDataType>({
    defaultValues: { name: '', lastname: '', phone: '', role: '' },
    resolver,
  })

  const roleId = watch('role')
  const isSecondaryOwnerSelected = !!secondaryOwnerRole && roleId === secondaryOwnerRole.id
  const isFormDirty = Object.keys(dirtyFields).length > 0 || isFarmsDirty

  useEffect(() => {
    if (isEdit && initialMember) {
      const [firstName, lastName] = initialMember.name!.split(' ')
      reset({
        name: firstName || '',
        lastname: lastName || '',
        phone: initialMember.phone || '',
        role: initialMember.user_role_id,
      })
      setSelectedFarms(initialMember?.member_scopes?.farms || [])
    }
  }, [isEdit, initialMember])

  useEffect(() => {
    if (userInfo?.id && roleId) {
      getRole({ userId: userInfo.id, roleId }).then((res) => {
        setPermissionRole(res.data.data.permissions)
      })
    }
  }, [userInfo?.id, roleId])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500)
    return () => clearTimeout(timer)
  }, [search])

  const { data: searchFarms, isFetching } = useFarmGroup(debouncedSearch)

  useEffect(() => {
    if (!isFetching) setFarms(searchFarms || [])
  }, [searchFarms, isFetching])

  const handlePondSelection = (pond: MemberScopePond, isChecked: boolean) => {
    setIsFarmsDirty(true)
    setSelectedFarms((prev) => {
      const updated = [...prev]
      const farm = farms.find((f) => f.ponds?.some((p) => p.id === pond.id))
      if (!farm) return prev

      const farmIndex = updated.findIndex((f) => f.id === farm.id)

      if (isChecked) {
        if (farmIndex > -1) {
          updated[farmIndex].ponds = [...(updated[farmIndex].ponds || []), pond]
        } else {
          updated.push({ ...farm, ponds: [pond] })
        }
      } else {
        if (farmIndex > -1) {
          updated[farmIndex].ponds =
            updated[farmIndex].ponds?.filter((p) => p.id !== pond.id) ?? null
          if (!updated[farmIndex].ponds?.length) updated.splice(farmIndex, 1)
        }
      }

      return updated
    })
  }

  const isPondChecked = (pond: MemberScopePond) =>
    selectedFarms.some((f) => f.ponds?.some((p) => p.id === pond.id))

  const getAccessDescription = (): string =>
    permissionRole
      .filter((p) => p.crud_permission[1] === 1)
      .map((p) => p.feature.name_th)
      .join(', ')

  const onCloseDialog = () => {
    reset({ name: '', lastname: '', phone: '', role: '' })
    setSelectedFarms([])
    setSearch('')
    setFarms([])
    setIsFarmsDirty(false)
    onClose()
  }

  const onSave = async () => {
    const valid = await trigger()
    if (!valid) return
    const name = `${watch('name')} ${watch('lastname')}`.trim()
    const scopes = isSecondaryOwnerSelected ? [] : transformFarmsData(selectedFarms)

    try {
      if (isEdit && initialMember && userInfo) {
        await updateMember.mutateAsync({
          userId: userInfo.id,
          memberId: initialMember.id,
          body: {
            name,
            phone: watch('phone'),
            user_role_id: watch('role'),
            scopes,
          } as UpdateMembersBody,
        })
      } else if (userInfo) {
        const res = await createMember.mutateAsync({
          userId: userInfo.id,
          body: {
            name,
            phone: watch('phone'),
            user_role_id: watch('role'),
            scopes,
          } as UpdateMembersBody,
        })
        if (res.status === 201) {
          // `role` = normalized role name only — never the invitee's
          // name/phone/email, which sit right next to this in scope.
          const roleName = roles?.find((r) => r.id === watch('role'))?.name
          const normalizedRole = normalizeRoleForAnalytics(roleName)
          trackEvent(
            AnalyticsEvents.memberInvited,
            normalizedRole ? { role: normalizedRole } : undefined
          )
        }
      }
      onCloseDialog()
      onSuccess?.()
    } catch (err: any) {
      if (
        err?.response?.status === 400 &&
        err?.response?.data?.message?.includes('PHONE_NUMBER_IS_EXIST')
      ) {
        setError('phone', { type: 'manual', message: t('member:phone.exist') })
      }
    }
  }

  return (
    <CustomDialog
      open={open}
      title={title}
      contentMinWidth={500}
      closeDialog={onCloseDialog}
      enableCloseButton
      enableInLineWithTitle
      submitLabel={t('common:submit')}
      formName="create-member-dialog"
      onSubmit={onSave}
    >
      <form>
        <Stack>
          {/* Personal Info Section */}
          <Grid container spacing={2}>
            {/* Name */}
            <Grid item xs={12} md={6}>
              <Typography className="input-title">
                <Box
                  component="span"
                  sx={{ color: 'error.main', mr: 0.5, fontWeight: 400, fontSize: 16 }}
                >
                  *
                </Box>
                {t('member:name.input')}
              </Typography>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    placeholder={t('member:name.input')}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />
            </Grid>

            {/* Lastname */}
            <Grid item xs={12} md={6}>
              <Typography className="input-title">
                <Box component="span" sx={{ color: 'error.main', mr: 0.5 }}>
                  *
                </Box>
                {t('member:lastname.input')}
              </Typography>
              <Controller
                name="lastname"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    placeholder={t('member:lastname.input')}
                    error={!!errors.lastname}
                    helperText={errors.lastname?.message}
                  />
                )}
              />
            </Grid>

            {/* Phone */}
            <Grid item xs={12} md={6}>
              <Typography className="input-title">
                <Box component="span" sx={{ color: 'error.main', mr: 0.5 }}>
                  *
                </Box>
                {t('member:phone.input')}
              </Typography>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    placeholder={t('member:phone.input')}
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                    inputProps={{ maxLength: 10, inputMode: 'numeric', pattern: '[0-9]*' }}
                    onChange={(e) => {
                      const onlyNums = e.target.value.replace(/[^0-9]/g, '').slice(0, 10)
                      field.onChange(onlyNums)
                    }}
                  />
                )}
              />
            </Grid>

            {/* Role */}
            <Grid item xs={12} md={6}>
              <Typography className="input-title">
                <Box component="span" sx={{ color: 'error.main', mr: 0.5 }}>
                  *
                </Box>
                {t('member:role.input')}
              </Typography>
              <Controller
                name="role"
                control={control}
                render={({ field }) =>
                  isOwnerMember ? (
                    <TextField fullWidth size="small" disabled value={RoleName.PrimaryOwner} />
                  ) : (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      size="small"
                      placeholder={t('member:role.input')}
                      error={!!errors.role}
                      helperText={errors.role?.message}
                    >
                      {roles
                        ?.filter((r) => r.name !== RoleName.PrimaryOwner)
                        .map((r) => (
                          <MenuItem key={r.id} value={r.id}>
                            {r.name}
                          </MenuItem>
                        ))}
                    </TextField>
                  )
                }
              />
            </Grid>
          </Grid>

          {/* Access Info */}
          {watch('role') && (
            <Box
              sx={{
                backgroundColor: 'info.background',
                borderRadius: 0.5,
                py: 0.5,
                px: 1,
                border: '1px solid',
                borderColor: 'info.border',
                mt: 2,
              }}
            >
              <Typography
                variant="body2"
                color="info.main"
                sx={{
                  fontWeight: 600,
                  fontSize: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                {t('member:access')}
              </Typography>
              <Typography variant="caption" color="info.main">
                {getAccessDescription() || '-'}
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 2, borderColor: 'divider' }} />

          {/* Farm / Pond Selection */}
          {isSecondaryOwnerSelected ? (
            <Typography variant="body2" color="text.secondary">
              {t('member:pond.all_access')}
            </Typography>
          ) : (
            <Box>
              <Typography sx={{ fontSize: 18, fontWeight: 600, mb: 1 }}>
                {t('member:pond.select')}
              </Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 400, mb: 1 }}>
                {t('common:search')}
              </Typography>
              {/* Search bar */}
              <TextField
                placeholder={t('common:search')}
                fullWidth
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon icon={searchIcon} width={20} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />

              {/* Farm sections */}
              {farms.map((farm) => {
                const ponds = farm.ponds || []
                const allSelected = ponds.every((p) => isPondChecked(p))
                const partiallySelected = ponds.some((p) => isPondChecked(p)) && !allSelected

                const handleFarmSelect = (checked: boolean) => {
                  ponds.forEach((pond) => handlePondSelection(pond, checked))
                }

                return (
                  <Accordion
                    key={farm.id}
                    disableGutters
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      overflow: 'hidden',
                      backgroundColor: 'common.white',
                      '&:before': { display: 'none' },
                    }}
                  >
                    {/* Farm header */}
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: '#D9D9D9' }} />}
                      sx={{
                        px: 2,
                        py: 0,
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: 'common.white',
                        '& .MuiAccordionSummary-content': {
                          margin: 0,
                        },
                      }}
                    >
                      <Checkbox
                        checked={allSelected}
                        indeterminate={partiallySelected}
                        onChange={(e) => {
                          e.stopPropagation()
                          handleFarmSelect(e.target.checked)
                        }}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          p: 0,
                          mr: 1,
                          color: '#D9D9D9',
                          '&.Mui-checked': {
                            color: 'primary.main',
                          },
                          '& .MuiCheckbox-indeterminate': {
                            color: '#D9D9D9',
                          },
                        }}
                      />
                      <Typography sx={{ fontWeight: 600, m: 0 }}>
                        {t('member:table.farm')} {farm.name || '-'}
                      </Typography>
                    </AccordionSummary>

                    {/* Pond list */}
                    <AccordionDetails sx={{ px: 3, pb: 2 }}>
                      <Divider sx={{ mb: 1, borderColor: 'divider' }} />
                      <Grid container spacing={1} sx={{ pl: 2 }}>
                        {ponds.map((pond) => (
                          <Grid item xs={12} sm={6} md={4} key={pond.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Checkbox
                                checked={isPondChecked(pond)}
                                onChange={(e) => handlePondSelection(pond, e.target.checked)}
                                sx={{
                                  color: '#D9D9D9',
                                  '&.Mui-checked': {
                                    color: 'primary.main',
                                  },
                                  '& .MuiCheckbox-indeterminate': {
                                    color: '#D9D9D9',
                                  },
                                }}
                              />
                              <ListItemText
                                primary={pond.name || '-'}
                                primaryTypographyProps={{
                                  fontSize: '16px',
                                  fontWeight: 400,
                                  color: 'text.primary',
                                }}
                              />
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                )
              })}
            </Box>
          )}
        </Stack>
      </form>
    </CustomDialog>
  )
}

export default CreateMemberDialog
